import { IRequestInstanceConfig } from "../clientRequests"
import { DocConfig } from "../forms"
import {
  ProfileAccess,
  RoleName,
  FieldConfigKey,
  AssociationId,
  FieldConfigItemSchema,
  ColumnConfig,
  ProcessConfigSchema,
  ProcessType,
} from "../../types"
import { addUnderScores, processTypes, removeUnderScores, toUpperCaseCustom } from "../../utils"
import { IPermissionConfig } from "../../interfaces"
import { IDataFlow } from "../../interfaces"
import { DEFAULT_PROFILE_TABLE_COLUMN_CONFIG } from "../../config/tableColumnConfig"

export type AssocationSettingsSchema = {
  association: AssociationId
  processConfig: ProcessConfigSchema
  allDataFlows: Record<string, IDataFlow>
  fieldConfig: Record<FieldConfigKey, FieldConfigItemSchema>
  docConfig: Record<string, DocConfig>
  permissionConfig?: Record<string, IPermissionConfig>
  deletedProfileTracker: string[]
  organisationConfig: Record<string, any>
  keyMaps: Record<string, Record<string, string>>
  availableExportConfigs: string[]
  allCompanyUsers: Record<string, string>[]
  requestConfig?: Record<string, any>
  profileAccess: Record<string, any>
  iam?: { permissions: Record<string, any>[] | null; version: number }
}

export class AssociationSettings {
  private association: AssociationId
  private allDataFlows: Record<string, IDataFlow>
  private availableExportConfigs: string[]
  private docConfig: Record<string, DocConfig>
  private fieldConfig: Record<string, FieldConfigItemSchema>
  private organisationConfig: Record<string, any>
  private processConfig: ProcessConfigSchema
  private profileAccess: ProfileAccess
  private deletedProfileTracker: string[]
  private iam: Record<string, any>
  private allCompanyUsers: Record<string, string>[]
  private requestConfig: Record<string, any>
  private allRoleNames: string[] = []

  constructor(obj: AssocationSettingsSchema) {
    this.association = obj.association || ""
    this.allDataFlows = obj.allDataFlows || {}
    this.availableExportConfigs = obj.availableExportConfigs || []
    this.docConfig = obj.docConfig || {}
    this.fieldConfig = obj.fieldConfig || {}
    this.organisationConfig = obj.organisationConfig || {}
    this.processConfig = obj.processConfig || {}
    this.profileAccess = obj.profileAccess || {}
    this.deletedProfileTracker = obj.deletedProfileTracker || []
    this.iam = obj.iam || {} //TODO: Move into UserRepo
    this.allCompanyUsers = obj.allCompanyUsers || []
    this.requestConfig = obj.requestConfig || {}
  }

  getAssociationSlug(): AssociationId {
    return this.association
  }

  getAllRoleNames() {
    return this.allRoleNames
  }

  getAllDataFlows(): Record<string, IDataFlow> {
    return this.allDataFlows
  }

  getAvailableExportConfigs(): string[] {
    return this.availableExportConfigs
  }

  getDocConfig(): Record<string, any> {
    return this.docConfig
  }

  getDisplayName(): string {
    return (
      toUpperCaseCustom(this.organisationConfig.displayName) || removeUnderScores(toUpperCaseCustom(this.association))
    )
  }

  getFieldConfig(): Record<string, FieldConfigItemSchema> {
    return this.fieldConfig
  }

  getOrganisationConfig(): Record<string, any> {
    return this.organisationConfig
  }

  getOrganisationConfigItem(itemName: string) {
    return this.organisationConfig[itemName]
  }

  getProfileTableColumnConfigWeb(): ColumnConfig[] {
    return this.organisationConfig.profileTableColumnConfigWeb || DEFAULT_PROFILE_TABLE_COLUMN_CONFIG
  }

  getProcessConfig(): ProcessConfigSchema {
    return this.processConfig
  }

  getProcessConfigItem(itemName: ProcessType): Record<string, any> {
    // DEPRECATED: Rather use the relevant method in ProcessConfig
    // @ts-ignore
    return this.getProcessConfig()[itemName]
  }

  getAllCohorts(): string[] {
    return Object.keys(this.profileAccess)
  }

  getProfileAccess(): Record<string, any> {
    return this.profileAccess
  }

  getDeletedProfileTracker(): string[] {
    return this.deletedProfileTracker
  }

  getIam(): Record<string, any> {
    return this.iam
  }

  getAllUsers(): Record<string, string>[] {
    return this.allCompanyUsers || []
  }

  getDocValidityEnabledStatus() {
    return !!this.getOrganisationConfig().docValidityEnabled
  }

  getRequestConfig(): Record<string, IRequestInstanceConfig> | undefined {
    if (!this.requestConfig) {
      return undefined
    }
    return this.requestConfig
  }

  setAllDataFlow(dataFlowName: string, config: IDataFlow): void {
    this.allDataFlows[dataFlowName] = config
  }

  setAvailableExportConfigs(availableExportConfigs: string[]): void {
    this.availableExportConfigs = availableExportConfigs
  }

  setDocConfig(docConfig: Record<string, DocConfig>): void {
    this.docConfig = docConfig
  }

  setFieldConfig(fieldConfig: Record<string, FieldConfigItemSchema>): void {
    this.fieldConfig = fieldConfig
  }

  setFieldConfigItem(fieldName: string, config: FieldConfigItemSchema): void {
    this.fieldConfig[fieldName] = config
  }

  setRoleNames(rolesNames: RoleName[]): void {
    this.allRoleNames = rolesNames
  }

  setOrganisationConfig(organisationConfig: Record<string, any>): void {
    this.organisationConfig = organisationConfig
  }

  setOrganisationConfigItem(itemName: string, config: any): void {
    this.organisationConfig[itemName] = config
  }

  setProcessConfigItem(item: Record<string, any>): void {
    const itemName = Object.keys(item)[0]
    if (this.processConfig.hasOwnProperty(itemName)) {
      // @ts-ignore
      this.processConfig[itemName] = item[itemName]
    }
  }

  setRequestConfig(requestConfig: Record<string, IRequestInstanceConfig>) {
    this.requestConfig = requestConfig
  }

  getConfiguredProcessInfo(
    configType: string,
    process: ProcessType,
    segmentInfo: any,
    configService: any,
    fallBackValue: any,
  ) {
    const processConfigTypeMap = {
      dataFlowNames: "dataCaptureConfig",
      docNames: "docCaptureConfig",
      actionFlow: "offenceConfig",
      actionExpiry: "offenceConfig",
      requiredCompetencies: "competencyConfig",
    } as Record<string, string>
    const {
      division,
      jobType,
      jobSubType,
      reasonForTermination,
      subReasonForTermination,
      eventType,
      action,
      offence,
      competencyName,
    } = segmentInfo
    let extractionConfig = [
      { targetKey: addUnderScores(division), fallbackKey: "ADMIN" },
      { targetKey: addUnderScores(jobType), fallbackKey: "allJobTypes" },
      { targetKey: addUnderScores(jobSubType), fallbackKey: "allJobSubTypes" },
    ]
    if (process === ProcessType.competencies) {
      if (processConfigTypeMap[configType] !== "competencyConfig") {
        extractionConfig = [
          ...extractionConfig,
          { targetKey: addUnderScores(competencyName), fallbackKey: "allCompetencies" },
        ]
      }
    }
    if (process === ProcessType.terminate) {
      extractionConfig = [
        ...extractionConfig,
        { targetKey: addUnderScores(reasonForTermination), fallbackKey: "allTerminationReasons" },
        { targetKey: addUnderScores(subReasonForTermination), fallbackKey: "allTerminationSubReasons" },
      ]
    }
    if (process === ProcessType.discipline) {
      if (processConfigTypeMap[configType] === "offenceConfig") {
        extractionConfig = [{ targetKey: offence, fallbackKey: "allOffences" }, ...extractionConfig]
      } else if (configType) {
        extractionConfig = [
          ...extractionConfig,
          { targetKey: offence, fallbackKey: "allOffences" },
          { targetKey: action, fallbackKey: "allActions" },
        ]
      }
    }
    if (process === ProcessType.eventConfig) {
      extractionConfig = [{ targetKey: eventType, fallbackKey: "allEventTypes" }]
    }
    extractionConfig.push({ targetKey: configType, fallbackKey: configType })
    const processConfig = this.getProcessConfig() as Record<string, any>

    const targetConfig = configService.extractTargetConfig(
      processConfig[process][processConfigTypeMap[configType]],
      extractionConfig,
      fallBackValue,
    )
    return targetConfig
  }
}
